import { useEffect, useState } from "react";
import { Coins } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";

/**
 * Iter 73 — Streamer ledger widget (Available / Reserved / Consumed tiles).
 * Iter 147 — Adds a whitelisted MOVEMENT history list below the tiles,
 * fed by GET /api/streamers/me/credit-history. Shows top-ups (crypto),
 * promotions, other admin grants, and chat-unlock refunds. Excludes
 * reserve / release / consume — those move via the tiles above.
 *
 * The list is framed as a movement log, not a reconciling statement:
 * the sum of visible deltas WILL NOT equal Available when a contract is
 * active (reserved/consumed movements are intentionally hidden). Copy
 * makes that explicit.
 */
export default function CreditLedger({ me }) {
  const { t } = useI18n();
  const L = me?.creditLedger || {};
  const avail = Number(L.availableCredits ?? me?.creditBalance ?? 0);
  const reserved = Number(L.reservedCredits ?? 0);
  const consumed = Number(L.consumedCredits ?? 0);
  const tiles = [
    { key: "available", label: t("ledger.available"), value: avail, tone: "text-[#2DD4A7] border-[#2DD4A7]/50 bg-[#2DD4A7]/[0.05]" },
    { key: "reserved",  label: t("ledger.reserved"),  value: reserved, tone: "text-[#F5B841] border-[#F5B841]/50 bg-[#F5B841]/[0.04]" },
    { key: "consumed",  label: t("ledger.consumed"),  value: consumed, tone: "text-[#8B97AE] border-white/20" },
  ];

  const [rows, setRows] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/streamers/me/credit-history")
      .then(({ data }) => { if (!cancelled) setRows(data?.rows || []); })
      .catch(() => { if (!cancelled) setRows([]); });
    return () => { cancelled = true; };
  }, [me?.id, avail, reserved, consumed]);

  const typeTone = (t) => t === "topup_rejected" || t === "chat_unlock"
    ? "text-[#F26D6D] border-[#F26D6D]/50"
    : t === "promotion"
      ? "text-[#F5B841] border-[#F5B841]/50"
      : t === "chat_refund"
        ? "text-[#2DD4A7] border-[#2DD4A7]/40"
        : "text-[#8B97AE] border-white/20";

  return (
    <section className="mt-8 border border-white/15 p-6 lg:p-8" data-testid="credit-ledger-section">
      <div className="flex items-center gap-2 mb-4"><Coins className="w-4 h-4 text-[#2DD4A7]" /><h2 className="font-display font-bold text-lg">{t("ledger.title")}</h2></div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {tiles.map((t) => (
          <div key={t.key} className={`border p-4 ${t.tone}`} data-testid={`ledger-tile-${t.key}`}>
            <div className="text-[10px] font-mono uppercase tracking-widest opacity-80">{t.label}</div>
            <div className="mt-1 font-display text-3xl font-black tracking-tight tabular-nums" data-testid={`ledger-value-${t.key}`} style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
              {Math.round(t.value).toLocaleString()}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6" data-testid="credit-history-section">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[11px] font-mono uppercase tracking-widest text-[#8B97AE]">{t("ledger.history.title")}</h3>
          <span className="text-[9px] font-mono uppercase tracking-widest text-[#8B97AE]">{t("ledger.history.sub")}</span>
        </div>
        <div className="text-[10px] font-mono text-[#8B97AE] mb-3">
          {t("ledger.history.desc")}
        </div>
        {rows === null ? (
          <div className="text-xs font-mono text-[#8B97AE]">{t("ledger.history.loading")}</div>
        ) : rows.length === 0 ? (
          <div className="text-xs font-mono text-[#8B97AE] border border-white/10 p-4 text-center" data-testid="credit-history-empty">{t("ledger.history.empty")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="credit-history-table">
              <thead className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">
                <tr className="border-b border-white/10">
                  <th className="text-left py-2 pr-3">{t("ledger.history.col.when")}</th>
                  <th className="text-left py-2 pr-3">{t("ledger.history.col.type")}</th>
                  <th className="text-right py-2 pr-3">{t("ledger.history.col.delta")}</th>
                  <th className="text-left py-2 pr-3">{t("ledger.history.col.reason")}</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {rows.map((r, i) => {
                  const positive = Number(r.delta || 0) >= 0;
                  return (
                    <tr key={i} className="border-b border-white/5" data-testid={`credit-history-row-${r.type}-${i}`}>
                      <td className="py-1.5 pr-3 text-[#8B97AE]">{(r.timestamp || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="py-1.5 pr-3">
                        <span className={`inline-block border px-1.5 py-0.5 text-[9px] uppercase tracking-widest ${typeTone(r.type)}`}>{r.label}</span>
                      </td>
                      <td className={`py-1.5 pr-3 text-right tabular-nums ${positive ? "text-[#F5B841]" : "text-[#F26D6D]"}`}>
                        {positive ? "+" : ""}{Number(r.delta || 0).toLocaleString()}
                      </td>
                      <td className="py-1.5 pr-3 text-[#E8ECF4] max-w-[420px] break-words">{r.reason_public}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
